import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  ACCEPTED_COOKIE_PREFERENCES,
  COOKIE_PREFERENCES_STORAGE_KEY,
  CookiePreferences,
  DEFAULT_COOKIE_PREFERENCES,
  isCookiePreferences,
} from '../privacy/cookie-preferences';

export interface CookieConsentState {
  preferences: CookiePreferences;
  hasSavedPreference: boolean;
  isReady: boolean;
  isDialogOpen: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly stateSubject: BehaviorSubject<CookieConsentState>;
  readonly state$: Observable<CookieConsentState>;

  private dialogOpener: HTMLElement | null = null;

  constructor() {
    const savedPreferences = this.readPreferences();

    this.stateSubject = new BehaviorSubject<CookieConsentState>({
      preferences: savedPreferences ?? { ...DEFAULT_COOKIE_PREFERENCES },
      hasSavedPreference: savedPreferences !== null,
      isReady: true,
      isDialogOpen: false,
    });
    this.state$ = this.stateSubject.asObservable();
  }

  get snapshot(): CookieConsentState {
    return this.stateSubject.value;
  }

  openPreferences(opener?: HTMLElement | null): void {
    this.dialogOpener =
      opener ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    this.updateState({ isDialogOpen: true });
  }

  closePreferences(): void {
    this.updateState({ isDialogOpen: false });
    this.restoreDialogFocus();
  }

  savePreferences(preferences: CookiePreferences): void {
    const shouldReload = this.snapshot.hasSavedPreference;
    const nextPreferences = { ...preferences };

    this.writePreferences(nextPreferences);
    this.stateSubject.next({
      ...this.snapshot,
      preferences: nextPreferences,
      hasSavedPreference: true,
      isDialogOpen: false,
    });
    this.restoreDialogFocus();

    if (shouldReload) {
      // Reloading removes any optional vendor scripts that were already active
      // and starts newly approved services from a clean document lifecycle.
      window.setTimeout(() => window.location.reload(), 0);
    }
  }

  acceptAll(): void {
    this.savePreferences(ACCEPTED_COOKIE_PREFERENCES);
  }

  rejectOptional(): void {
    this.savePreferences(DEFAULT_COOKIE_PREFERENCES);
  }

  private updateState(update: Partial<CookieConsentState>): void {
    this.stateSubject.next({ ...this.snapshot, ...update });
  }

  private restoreDialogFocus(): void {
    window.setTimeout(() => {
      this.dialogOpener?.focus();
      this.dialogOpener = null;
    }, 0);
  }

  private readPreferences(): CookiePreferences | null {
    try {
      const rawValue = window.localStorage.getItem(
        COOKIE_PREFERENCES_STORAGE_KEY
      );

      if (!rawValue) {
        return null;
      }

      const parsed = JSON.parse(rawValue) as unknown;

      return isCookiePreferences(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private writePreferences(preferences: CookiePreferences): void {
    try {
      window.localStorage.setItem(
        COOKIE_PREFERENCES_STORAGE_KEY,
        JSON.stringify(preferences)
      );
    } catch {
      // Some private browsing modes block storage. The in-memory choice still
      // applies for the current document and the banner returns next visit.
    }
  }
}
